import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import LoginService from '../services/LoginService';
import type { LoginResponse } from '../shared/interfaces/LoginInterface';
import type { UserDto } from '../shared/interfaces/UsersInterfaces';
import type { ApiResponse } from '../shared/interfaces/ApiResponseInterface';

interface AuthState {
  user: UserDto | null;
  accessToken: string | null;
  refreshToken: string | null;
  authorities: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<ApiResponse<LoginResponse>>;
  loginWithGoogle: (token: string) => Promise<ApiResponse<LoginResponse>>;
  logout: () => void;
  refreshAccessToken: () => Promise<boolean>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string }>;
  clearError: () => void;
  getRedirectPath: () => string;
  hasAuthority: (authority: string) => boolean;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message?: string }>;
  hasAnyAuthority: (authorities: string[]) => boolean;
  hasAllAuthorities: (authorities: string[]) => boolean;
  hasAuthorities: (authorities: string[], requireAll?: boolean) => boolean;
  setHasHydrated: (value: boolean) => void;
}

const ROLE_ROUTES: Record<string, string> = {
  ADMIN: '/dashboard/view',
  RH: '/dashboard/view',
  MANAGER: '/dashboard/view',
  DEPARTEMENT_CHIEF: '/dashboard/view',
  DG: '/dashboard/view',
  EMPLOYE: '/dashboard/view',
};

const DEFAULT_ROUTE = '/dashboard/view';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      authorities: [],
      isAuthenticated: false,
      isLoading: false,
      isHydrated: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        const response = await LoginService.login({ email, password });

        if (response.success && response.data) {
          set({
            user: response.data.user,
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            authorities: response.data.permissions || [],
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          set({
            isLoading: false,
            isAuthenticated: false,
            error: response.message || 'Email ou mot de passe incorrect',
          });
        }

        return response;
      },

      loginWithGoogle: async (token) => {
        set({ isLoading: true, error: null });
        const response = await LoginService.googleLogin(token);

        if (response.success && response.data) {
          set({
            user: response.data.user,
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            authorities: response.data.permissions || [],
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          set({
            isLoading: false,
            isAuthenticated: false,
            error: response.message || 'Erreur lors de la connexion Google',
          });
        }

        return response;
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          authorities: [],
          isAuthenticated: false,
          error: null,
        });
      },

      refreshAccessToken: async () => {
        const { refreshToken, logout } = get();
        if (!refreshToken) {
          logout();
          return false;
        }

        const response = await LoginService.refreshToken(refreshToken);

        if (response.success && response.data) {
          set({ accessToken: response.data });
          return true;
        }

        logout();
        return false;
      },

      resetPassword: async (token, password) => {
        set({ isLoading: true, error: null });

        try {
          const response = await LoginService.resetPassword(token, password);

          if (response.success) {
            set({ isLoading: false, error: null });
            return { success: true, message: response.message };
          } else {
            set({
              isLoading: false,
              error: response.message || 'Une erreur est survenue',
            });
            return {
              success: false,
              message: response.message || 'Une erreur est survenue',
            };
          }
        } catch (error: any) {
          const message = error.response?.data?.message || 'Une erreur est survenue';
          set({ isLoading: false, error: message });
          return { success: false, message };
        }
      },

      forgotPassword: async (email) => {
        set({ isLoading: true, error: null });

        try {
          const response = await LoginService.forgotPassword(email);

          if (response.success) {
            set({ isLoading: false, error: null });
            return { success: true, message: response.message };
          } else {
            set({
              isLoading: false,
              error: response.message || 'Une erreur est survenue',
            });
            return {
              success: false,
              message: response.message || 'Une erreur est survenue',
            };
          }
        } catch (error: any) {
          const message = error.response?.data?.message || 'Une erreur est survenue';
          set({ isLoading: false, error: message });
          return { success: false, message };
        }
      },

      clearError: () => set({ error: null }),

      getRedirectPath: () => {
        const { user } = get();
        if (!user?.role?.name) return DEFAULT_ROUTE;
        return ROLE_ROUTES[user.role.name] || DEFAULT_ROUTE;
      },

      updatePassword: async (currentPassword: string, newPassword: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await LoginService.updatePassword(currentPassword, newPassword);

          if (response.success) {
            set({ isLoading: false, error: null });
            return { success: true, message: response.message };
          } else {
            set({
              isLoading: false,
              error: response.message || 'Une erreur est survenue',
            });
            return {
              success: false,
              message: response.message || 'Une erreur est survenue',
            };
          }
        } catch (error: any) {
          const message = error.response?.data?.message || 'Une erreur est survenue';
          set({ isLoading: false, error: message });
          return { success: false, message };
        }
      },

      hasAuthority: (authority) => get().authorities.includes(authority),

      hasAnyAuthority: (authorities) => authorities.some((auth) => get().authorities.includes(auth)),

      hasAllAuthorities: (authorities) => authorities.every((auth) => get().authorities.includes(auth)),

      hasAuthorities: (authorities, requireAll = true) => {
        if (!authorities?.length) return true;
        return requireAll ? get().hasAllAuthorities(authorities) : get().hasAnyAuthority(authorities);
      },

      setHasHydrated: (value) => set({ isHydrated: value }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        authorities: state.authorities,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);